/**
 * BodyRegionMap Component
 *
 * Read-only SVG body visualization with risk-tier-colored region highlights.
 * Renders front + back silhouettes side-by-side so quads/hamstrings,
 * chest/back, etc. each have their own anatomical view.
 *
 * @component
 */

import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RegionalPainEntry {
  region: string;
  pain_level: number;
  pain_type: string;
  is_new_onset: boolean;
  report_date: string;
  previous_injury?: {
    days_ago: number;
    severity: string;
    is_active: boolean;
  };
}

interface BodyRegionMapProps {
  data: RegionalPainEntry[];
  isDark: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REGION_LABELS: Record<string, string> = {
  head_neck: 'Head / Neck',
  left_shoulder: 'L Shoulder',
  right_shoulder: 'R Shoulder',
  upper_back: 'Upper Back',
  lower_back: 'Lower Back',
  left_hip: 'L Hip',
  right_hip: 'R Hip',
  left_quad: 'L Quad',
  right_quad: 'R Quad',
  left_hamstring: 'L Hamstring',
  right_hamstring: 'R Hamstring',
  left_knee: 'L Knee',
  right_knee: 'R Knee',
  left_ankle: 'L Ankle',
  right_ankle: 'R Ankle',
  left_calf: 'L Calf',
  right_calf: 'R Calf',
  other: 'Other',
};

/** Which view each region appears on. */
export const FRONT_REGIONS = new Set([
  'head_neck', 'left_shoulder', 'right_shoulder',
  'left_hip', 'right_hip', 'left_quad', 'right_quad',
  'left_knee', 'right_knee', 'left_ankle', 'right_ankle',
]);

export const BACK_REGIONS = new Set([
  'upper_back', 'lower_back', 'left_hamstring', 'right_hamstring',
  'left_calf', 'right_calf',
]);

/**
 * Hit-area positions relative to each body silhouette's local coordinate space.
 * The silhouette is drawn in a 100×200 viewBox.
 */
export const FRONT_POSITIONS: Record<string, { x: number; y: number; w: number; h: number }> = {
  head_neck:       { x: 50, y: 18, w: 26, h: 30 },
  left_shoulder:   { x: 26, y: 52, w: 18, h: 16 },
  right_shoulder:  { x: 74, y: 52, w: 18, h: 16 },
  left_hip:        { x: 36, y: 105, w: 16, h: 14 },
  right_hip:       { x: 64, y: 105, w: 16, h: 14 },
  left_quad:       { x: 37, y: 128, w: 14, h: 28 },
  right_quad:      { x: 63, y: 128, w: 14, h: 28 },
  left_knee:       { x: 37, y: 158, w: 13, h: 12 },
  right_knee:      { x: 63, y: 158, w: 13, h: 12 },
  left_ankle:      { x: 37, y: 188, w: 12, h: 10 },
  right_ankle:     { x: 63, y: 188, w: 12, h: 10 },
};

export const BACK_POSITIONS: Record<string, { x: number; y: number; w: number; h: number }> = {
  upper_back:      { x: 50, y: 62, w: 28, h: 22 },
  lower_back:      { x: 50, y: 88, w: 26, h: 18 },
  left_hamstring:  { x: 37, y: 134, w: 14, h: 28 },
  right_hamstring: { x: 63, y: 134, w: 14, h: 28 },
  left_calf:       { x: 37, y: 170, w: 13, h: 24 },
  right_calf:      { x: 63, y: 170, w: 13, h: 24 },
};

export function painColor(level: number): string {
  if (level <= 3) return '#22c55e';
  if (level <= 5) return '#eab308';
  if (level <= 7) return '#f97316';
  return '#ef4444';
}

export function regionColor(entry: RegionalPainEntry): { color: string; pulse: boolean } {
  if (entry.previous_injury) {
    if (entry.previous_injury.is_active) {
      return { color: '#ef4444', pulse: true };
    }
    if (entry.previous_injury.days_ago <= 30) {
      return { color: '#f97316', pulse: false };
    }
    if (entry.previous_injury.days_ago <= 90) {
      return { color: '#eab308', pulse: false };
    }
  }
  return { color: painColor(entry.pain_level), pulse: false };
}

function painTier(level: number): string {
  if (level <= 3) return 'Mild';
  if (level <= 5) return 'Moderate';
  if (level <= 7) return 'Elevated';
  return 'Severe';
}

// ---------------------------------------------------------------------------
// Body Silhouette SVG (shared between front & back views)
// ---------------------------------------------------------------------------

interface SilhouetteProps {
  fillColor: string;
  opacity: number;
}

export const BodySilhouette: React.FC<SilhouetteProps> = ({ fillColor, opacity }) => (
  <g fill={fillColor} opacity={opacity}>
    {/* Head */}
    <ellipse cx="50" cy="18" rx="12" ry="14" />
    {/* Neck */}
    <rect x="45" y="32" width="10" height="8" rx="2" />
    {/* Torso */}
    <rect x="33" y="40" width="34" height="60" rx="5" />
    {/* Left arm */}
    <rect x="20" y="44" width="11" height="42" rx="4" />
    {/* Right arm */}
    <rect x="69" y="44" width="11" height="42" rx="4" />
    {/* Left leg */}
    <rect x="34" y="102" width="14" height="54" rx="4" />
    {/* Right leg */}
    <rect x="52" y="102" width="14" height="54" rx="4" />
    {/* Left shin */}
    <rect x="34" y="158" width="13" height="36" rx="4" />
    {/* Right shin */}
    <rect x="53" y="158" width="13" height="36" rx="4" />
  </g>
);

// ---------------------------------------------------------------------------
// Region overlay renderer
// ---------------------------------------------------------------------------

interface RegionOverlaysProps {
  entries: RegionalPainEntry[];
  positions: Record<string, { x: number; y: number; w: number; h: number }>;
}

const RegionOverlays: React.FC<RegionOverlaysProps> = ({ entries, positions }) => (
  <>
    {entries.map(entry => {
      const pos = positions[entry.region];
      if (!pos) return null;
      const { color, pulse } = regionColor(entry);
      return (
        <g key={entry.region}>
          <rect
            x={pos.x - pos.w / 2}
            y={pos.y - pos.h / 2}
            width={pos.w}
            height={pos.h}
            rx={3}
            fill={color}
            fillOpacity={0.4}
            stroke={color}
            strokeWidth={1}
          >
            {pulse && (
              <animate
                attributeName="fill-opacity"
                values="0.4;0.7;0.4"
                dur="1.5s"
                repeatCount="indefinite"
              />
            )}
          </rect>
          <text
            x={pos.x}
            y={pos.y + 4}
            textAnchor="middle"
            fontSize="9"
            fontWeight="bold"
            fill={color}
          >
            {entry.pain_level}
          </text>
        </g>
      );
    })}
  </>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BodyRegionMap: React.FC<BodyRegionMapProps> = ({ data, isDark }) => {
  if (!data || data.length === 0) {
    return (
      <div className={`text-xs text-center py-3 rounded-lg ${isDark ? 'bg-neutral-800/50 text-neutral-500' : 'bg-gray-50 text-gray-400'}`}>
        No localized soreness data -- Whole-body assessment only
      </div>
    );
  }

  const frontEntries = data.filter(e => FRONT_REGIONS.has(e.region));
  const backEntries = data.filter(e => BACK_REGIONS.has(e.region));

  const silFill = isDark ? '#525252' : '#d1d5db';
  const labelColor = isDark ? '#a3a3a3' : '#9ca3af';

  return (
    <div className="mb-4">
      <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-neutral-200' : 'text-gray-800'}`}>
        Body Region Risk Map
      </h4>

      <div className="flex gap-4">
        {/* Dual body silhouettes */}
        <div className="flex-shrink-0 flex gap-2">
          {/* Front view */}
          <div className="text-center">
            <svg viewBox="0 0 100 200" className="w-[80px] h-[160px]">
              <BodySilhouette fillColor={silFill} opacity={0.3} />
              <RegionOverlays entries={frontEntries} positions={FRONT_POSITIONS} />
            </svg>
            <span className="text-[10px] block mt-0.5" style={{ color: labelColor }}>Front</span>
          </div>

          {/* Back view */}
          <div className="text-center">
            <svg viewBox="0 0 100 200" className="w-[80px] h-[160px]">
              <BodySilhouette fillColor={silFill} opacity={0.3} />
              <RegionOverlays entries={backEntries} positions={BACK_POSITIONS} />
            </svg>
            <span className="text-[10px] block mt-0.5" style={{ color: labelColor }}>Back</span>
          </div>
        </div>

        {/* Region detail cards */}
        <div className="flex-1 space-y-2 min-w-0">
          {data
            .sort((a, b) => b.pain_level - a.pain_level)
            .map(entry => (
              <div
                key={entry.region}
                className={`p-2 rounded-lg border text-xs ${
                  isDark
                    ? 'bg-neutral-800/50 border-neutral-700/50'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-semibold ${isDark ? 'text-neutral-200' : 'text-gray-800'}`}>
                    {REGION_LABELS[entry.region] || entry.region}
                  </span>
                  <span className="font-bold" style={{ color: painColor(entry.pain_level) }}>
                    {entry.pain_level}/10 {painTier(entry.pain_level)}
                  </span>
                </div>
                <div className={`flex items-center gap-2 ${isDark ? 'text-neutral-400' : 'text-gray-500'}`}>
                  <span className="capitalize">{entry.pain_type}</span>
                  {entry.is_new_onset && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                    }`}>
                      New
                    </span>
                  )}
                </div>
                {entry.previous_injury && (
                  <div className={`mt-1 pt-1 border-t ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
                    <span className={`text-[10px] ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                      Previous {entry.previous_injury.severity} injury {entry.previous_injury.days_ago}d ago
                      {entry.previous_injury.is_active && ' (active)'}
                    </span>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Legend */}
      <div className={`flex flex-wrap gap-3 mt-3 text-[10px] ${isDark ? 'text-neutral-400' : 'text-gray-500'}`}>
        {[
          { color: '#ef4444', label: 'Active Injury' },
          { color: '#f97316', label: 'Recent History (<30d)' },
          { color: '#eab308', label: 'Older History (<90d)' },
          { color: '#22c55e', label: 'Pain Only' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: color }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
